import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from '@auth0/auth0-react';

// react-router components
import { Routes, Route } from "react-router-dom"; // Removed BrowserRouter from here
import theme from "assets/theme";
import { useSoftUIController, setDeviceLayout } from "hooks/context";

// components
import { ThemeProvider } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material";
import SoftBox from "components/SoftComponents/SoftBox";

// mapbox and routes
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import routes from "page_routes_copy";
import userAPI from "services/api/user";
import { useQuery } from '@tanstack/react-query';

import PageLayout from "layouts/PageLayout";
import BaseNavbar from "layouts/BaseNavbar";

import { setMapIcons } from "utils/map";
import moment from "moment";

const getActiveMode = (userMode) => {
    if (userMode !== "system") return userMode;
    const hour = moment().hour();
    return (hour >= 18 || hour < 6) ? "dark" : "light";
};
import SoftTypography from "components/SoftComponents/SoftTypography";
import MapView from "pages/views/MapView";
import { loadToken } from "services/api/client";
import { setUserPermissions } from "hooks/context";
import PermissionGuard from "components/Routes/PermissionGuard";

export default function App() {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [controller, dispatch] = useSoftUIController();
    const { deviceLayout } = controller;
    const [isLoaded, setIsLoaded] = useState(false);

    // 1. Pulled out 'error' from useAuth0 to watch for failures
    const { isAuthenticated, user, isLoading: isAuthLoading, error, loginWithRedirect, getAccessTokenSilently } = useAuth0();

    const { data: selected_user, isLoading: isUserLoading } = useQuery({
        queryKey: ['getUser', user?.email],
        enabled: !!user?.email,
        queryFn: async () => {
            return await userAPI.getUser();
        }
    });

    const userPermissions = useMemo(() => {
        var perms = selected_user?.permissions || [];
        return perms;
    }, [selected_user?.organisations]);

    const activeVisualMode = useMemo(() =>
        getActiveMode(selected_user?.user?.mode),
        [selected_user?.user?.mode]);

    // 2. Automated Redirect Effect for unauthenticated users
    useEffect(() => {
        if (!isAuthLoading && !isAuthenticated && !error) {
            loginWithRedirect({
                appState: { returnTo: window.location.pathname }
            });
        }
    }, [isAuthLoading, isAuthenticated, error, loginWithRedirect]);

    useEffect(() => {
        if (isAuthenticated) {
            loadToken({ getAccessTokenSilently, loginWithRedirect });
        }
    }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

    useEffect(() => {
        if (!map.current && mapContainer.current && selected_user) {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: activeVisualMode === "dark" ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/harrymontano/cmn5irh6q001o01snbw5yembt?optimize=true',
                projection: 'globe',
                center: [151, -33],
                interactive: false,
                antialias: true
            });

            map.current.on('load', () => {
                map.current.originalCenter = map.current.getCenter();
                if (activeVisualMode === "dark") {
                    map.current.setFog({
                        color: 'rgba(255,255,255,0.06)',
                        'high-color': 'rgba(170,190,255,0.25)',
                        'space-color': '#05070f',
                        'horizon-blend': 0.18,
                        'star-intensity': 0.25
                    });
                }
                else {
                    map.current.setFog({
                        'color': '#ffffff',
                        'high-color': '#444444',
                        'space-color': '#242424',
                        'horizon-blend': 0.02,
                        'star-intensity': 0.5
                    });
                }
                setIsLoaded(true);
            });
        }
    }, [selected_user]);

    useEffect(() => {
        if (map.current && isLoaded) {
            const newStyle = activeVisualMode === "dark" ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/harrymontano/cmn5irh6q001o01snbw5yembt?optimize=true';
            // console.log(newStyle, map.current.getStyle().sprite.replace('sprite', 'style'))
            if (newStyle != map.current.getStyle().sprite.replace('sprite', 'style')) {
                map.current.setStyle(newStyle);
                map.current.once('style.load', () => {
                    if (activeVisualMode === "dark") {
                        map.current.setFog({
                            color: 'rgba(255,255,255,0.06)',
                            'high-color': 'rgba(170,190,255,0.25)',
                            'space-color': '#05070f',
                            'horizon-blend': 0.18,
                            'star-intensity': 0.25
                        });
                    }
                    else {
                        map.current.setFog({
                            'color': '#ffffff',
                            'high-color': '#444444',
                            'space-color': '#242424',
                            'horizon-blend': 0.02,
                            'star-intensity': 0.5
                        });
                    }
                    setMapIcons(map, false);
                });
            }
        }
    }, [activeVisualMode, isLoaded]);

    useEffect(() => {
        function handleDeviceLayout() {
            setDeviceLayout(dispatch, window.innerWidth < 1200 ? "mobile" : "desktop");
        }
        window.addEventListener("resize", handleDeviceLayout);
        handleDeviceLayout();
        return () => window.removeEventListener("resize", handleDeviceLayout);
    }, [dispatch]);

    useEffect(() => {
        if (selected_user && userPermissions.length > 0) {
            setUserPermissions(dispatch, userPermissions);
        }
    }, [dispatch, selected_user, userPermissions]);

    const processedRoutesConfig = useMemo(() => {
        if (!selected_user) return routes;

        return routes.map((route) => {
            if (route?.key && route?.key.includes("overview")) {
                const { collapse, ...parentOnly } = route;
                return {
                    ...parentOnly,
                    component: MapView,
                    route: "/overview",
                    in_menu: true
                };
            }
            return route;
        });
    }, [routes, selected_user]);

    const allRoutes = useMemo(() => {
        if (!selected_user) return [];
        return processedRoutesConfig.flatMap(route => {
            const { collapse, ...parent } = route;
            if (!collapse) return parent;
            return [
                parent,
                ...collapse.map(child => ({ ...child, parentKey: parent.key })),
            ];
        });
    }, [processedRoutesConfig, selected_user]);

    const renderRoutes = useMemo(() => {
        if (!selected_user) return [];

        return allRoutes.map(function (route, key) {
            const Component = route.component;

            if (!Component) {
                console.error(`Route at index ${key} has an undefined component!`, route);
                return null;
            }

            return (
                <Route
                    exact
                    path={route.route}
                    key={key}
                    element={
                        /* Pipe permissions down cleanly inside your current wrapper */
                        <PermissionGuard user={selected_user} userPermissions={userPermissions}>
                            <Component map={map} user={selected_user} />
                        </PermissionGuard>
                    }
                />
            );
        });
    }, [selected_user, allRoutes, userPermissions]);

    // 3. Catch Critical Auth0 configurations errors and handle automatic reset
    if (error) {
        // We use a useEffect inside a wrapper component or handle it immediately 
        // string-checking/redirecting via window.location
        setTimeout(() => {
            window.location.href = window.location.origin; // Clears URL params and reloads at '/'
        }, 3000); // 3-second delay so they can read the message

        return (
            <ThemeProvider theme={theme}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#1a1a1a', color: 'white' }}>
                    <SoftTypography variant="h4" color="error">An Error has occurred</SoftTypography>
                    <SoftTypography variant="body2" mt={1} color="light">
                        {error.message || "Authentication failed."}
                    </SoftTypography>
                    <SoftTypography variant="caption" mt={2} sx={{ opacity: 0.5 }}>
                        Redirecting you back to the home page to try again...
                    </SoftTypography>
                </Box>
            </ThemeProvider>
        );
    }

    // Show app loading state while Auth0 authenticates or TanStack user details resolve
    if (isAuthLoading || (isAuthenticated && isUserLoading)) {
        return (
            <ThemeProvider theme={theme}>
                <Box sx={({ palette: { gradients }, functions: { linearGradient } }) => ({
                    background: linearGradient(gradients.dark.main, gradients.dark.state),
                    height: '100%',
                    width: '100%',
                    position: 'fixed',
                    top: '0px',
                    left: '0px',
                    zIndex: 9999,
                    overflow: 'hidden'
                })}>
                    <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100%">
                        <CircularProgress color="light" />
                        <SoftTypography variant="button" fontWeight="medium" mt={2} color="light">
                            Loading Application...
                        </SoftTypography>
                    </SoftBox>
                </Box>
            </ThemeProvider>
        );
    }
    // 4. Removed the 'if (!isLoaded) return null;' statement.
    // We must let the component render so mapContainer.current mounts into the DOM,
    // allowing your Mapbox hook to run successfully.
    return (
        <ThemeProvider theme={theme}>
            <Box component="div" ref={mapContainer}
                sx={({ palette: { gradients }, functions: { linearGradient } }) => ({
                    background: linearGradient(gradients.dark.main, gradients.dark.state),
                    height: '100%',
                    width: '100%',
                    position: 'fixed',
                    top: '0px',
                    left: '0px',
                    zIndex: 0,
                    overflow: 'hidden'
                })}
            />
            {isAuthenticated && selected_user && (
                <PageLayout>
                    <BaseNavbar
                        routes={processedRoutesConfig}
                        user={selected_user}
                        dark
                        menu={true}
                    />
                    <Routes>
                        {isLoaded && renderRoutes}
                    </Routes>
                </PageLayout>
            )}
        </ThemeProvider>
    );
}